import React from "react";
import { Input, Button, Collapse, theme } from "antd";
import { PlusOutlined, DeleteOutlined, HolderOutlined } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useResumeStore } from "../../store/resumeStore";
import { FIELD_SPELLCHECK_PROPS } from "./resumeQuality";

const { Panel } = Collapse;
const { useToken } = theme;

export default function SkillsEditor() {
  const { token } = useToken();

  // ✅ Subscribe only to what we need
  const skillGroups = useResumeStore((s) => s.resume.skillGroups);
  const updateResume = useResumeStore((s) => s.updateResume);

  const skills = skillGroups?.skills || [];

  // ---------------- ADD ----------------
  const addItem = () => {
    updateResume("skillGroups", {
      ...skillGroups,
      skills: [...skills, ""],
    });
  };

  // ---------------- UPDATE ----------------
  const updateItem = (index, value) => {
    const newSkills = [...skills];
    newSkills[index] = value;

    updateResume("skillGroups", {
      ...skillGroups,
      skills: newSkills,
    });
  };

  // ---------------- REMOVE ----------------
  const removeItem = (index) => {
    const newSkills = skills.filter((_, i) => i !== index);

    updateResume("skillGroups", {
      ...skillGroups,
      skills: newSkills,
    });
  };

  // ---------------- DRAG ----------------
  const onDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(skills);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    updateResume("skillGroups", {
      ...skillGroups,
      skills: items,
    });
  };

  return (
    <div>
      <h3 style={{ marginBottom: 16, color: token.colorTextHeading }}>
        Skills
      </h3>

      <DragDropContext onDragEnd={onDragEnd}>
        <Collapse accordion ghost defaultActiveKey={["skills"]}>
          <Panel key="skills" header="Skills">
            <Droppable droppableId="skills">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {skills.map((item, index) => (
                    <Draggable
                      key={`skill-${index}`}
                      draggableId={`skill-${index}`}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 8,
                            ...provided.draggableProps.style,
                          }}
                        >
                          <span
                            {...provided.dragHandleProps}
                            aria-label="Drag skill"
                            title="Drag to reorder"
                            style={{
                              alignItems: "center",
                              background: "rgba(22, 119, 255, 0.1)",
                              border: `1px solid ${token.colorBorderSecondary}`,
                              borderRadius: 8,
                              color: token.colorPrimary,
                              cursor: "grab",
                              display: "inline-flex",
                              flexShrink: 0,
                              fontSize: 16,
                              height: 32,
                              justifyContent: "center",
                              width: 32,
                            }}
                          >
                            <HolderOutlined />
                          </span>

                          <Input
                            value={item}
                            onChange={(e) =>
                              updateItem(index, e.target.value)
                            }
                            {...FIELD_SPELLCHECK_PROPS}
                          />

                          <DeleteOutlined
                            style={{
                              color: token.colorError,
                              cursor: "pointer",
                            }}
                            onClick={() => removeItem(index)}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}

                  {provided.placeholder}
                </div>
              )}
            </Droppable>

            <Button
              type="dashed"
              block
              icon={<PlusOutlined />}
              style={{ marginTop: 8 }}
              onClick={addItem}
            >
              Add Skill
            </Button>
          </Panel>
        </Collapse>
      </DragDropContext>
    </div>
  );
}
